import { css } from 'aphrodite';
import React, { useId, useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { formatCurrency } from '../../../helpers/formatCurrency';
import type { CloseMonthRow } from '../../helpers/opportunityReportingChartTypes';
import { areaChartCardStyles } from '../areaChartCardStyles';
import { styleSheet } from './styles';

const CHART_AXIS_TICK = { fontSize: THEME.typography.fontSizes.xxs, fill: THEME.colors.text.main } as const;

function CreationDateByMonthTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: CloseMonthRow & { formattedLabel: string } }>;
}) {
	if (!active || !payload?.length) {
		return null;
	}
	const row = payload[0]?.payload;
	if (!row) {
		return null;
	}
	return (
		<div className={css(styleSheet.tooltip)}>
			<p className={css(styleSheet.tooltipLabel)}>{row.label}</p>
			<p className={css(styleSheet.tooltipValue)}>{row.count} created</p>
			<p className={css(styleSheet.tooltipValue)}>{formatCurrency(row.dealSize)} current deal size</p>
		</div>
	);
}

interface CreationDateByMonthChartProps {
	data: CloseMonthRow[];
	isLoading?: boolean;
	className?: string;
}

export const CreationDateByMonthChart: React.FC<CreationDateByMonthChartProps> = ({ data, isLoading, className }) => {
	const gradientId = `creationMonthGrad-${useId().replaceAll(':', '')}`;
	const chartData = useMemo(
		() =>
			data.map(d => ({
				...d,
				formattedLabel: d.label,
			})),
		[data]
	);

	if (isLoading) {
		return (
			<div className={`${css(areaChartCardStyles.container)} ${className || ''}`}>
				<div className={css(areaChartCardStyles.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(areaChartCardStyles.container)} ${className || ''}`}>
				<div className={css(areaChartCardStyles.empty)}>No opportunities with a creation date in the loaded data</div>
			</div>
		);
	}

	return (
		<div className={`${css(areaChartCardStyles.container)} ${className || ''}`}>
			<h2 className={css(areaChartCardStyles.title)}>Created by month</h2>
			<ResponsiveContainer width='100%' height={280}>
				<AreaChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
					<defs>
						<linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
							<stop offset='5%' stopColor={THEME.colors.chart.levBlue} stopOpacity={0.8} />
							<stop offset='95%' stopColor={THEME.colors.chart.levBlue} stopOpacity={0} />
						</linearGradient>
					</defs>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis dataKey='formattedLabel' stroke={THEME.colors.text.main} tick={CHART_AXIS_TICK} />
					<YAxis allowDecimals={false} stroke={THEME.colors.text.main} tick={CHART_AXIS_TICK} />
					<Tooltip content={<CreationDateByMonthTooltip />} />
					<Area
						type='monotone'
						dataKey='count'
						stroke={THEME.colors.primary.main}
						fillOpacity={1}
						fill={`url(#${gradientId})`}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
};
